import '../../components/calendar/ReactHorizontalDatePicker.css'
import ReactHorizontalDatePicker from '../../components/calendar/ReactHorizontalDatePicker'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import Snackbar from '@material-ui/core/Snackbar'
import { ThemeProvider } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import { navigate, PageRendererProps } from 'gatsby'
import { DateTime } from 'luxon'
import Tooltip from '@material-ui/core/Tooltip'
import React, { useEffect, useState } from 'react'
import UsersAttending from '../../components/live/usersAttending'
import { theme } from '../../constants/colors'
import {
  ATHLETE_LIVE_WORKOUT_DETAIL,
  TRAINER_EDIT_WORKOUT,
  LIVE_WORKOUT_DETAIL,
  TRAINER_DETAIL,
  TRAINER_RECORDINGS,
} from '../../constants/routes'
import * as S from '../../styles/schedules.styles'
import * as Colors from '../../constants/colors'
import { ILiveWorkout, LiveConfig } from '../../types'
import {
  deleteLiveWorkout,
  fetchLiveWorkouts,
  signupLiveWorkout,
  fetchLiveConfig,
} from '../../util/fetch/workouts'
import * as session from '../../util/session'
import { connect } from 'react-redux'
import { RootState } from '../../redux/reducers/rootReducers'
import JoinButton from '../../components/live/joinButton'
import { TrainerResponse } from '../../redux/actions'
import { getId } from '../../services/session'

const GATSBY_API_BASE_CLIENT =
  process.env.GATSBY_API_BASE_CLIENT === 'true' ? true : false

const daysAddedToSelectedDate = [1, 2, 3, 4, 5, 6, 7]

type Props = ReturnType<typeof mapStateToProps> &
  PageRendererProps & {
    trainerId?: number
    userProfileId?: number
    fullWidth?: boolean
  }

const GATSBY_LIVE_FEATURE_TOGGLE =
  process.env.GATSBY_LIVE_FEATURE_TOGGLE === 'true'

const ScheduleComponent = (props: Props): React.ReactElement => {
  const today = DateTime.fromJSDate(new Date())
  const [workouts, setWorkouts] = useState<ILiveWorkout[]>([])
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(today)
  const [loading, setLoading] = useState(true)
  const [openSnackbar, setOpenSnackbar] = useState(false)
  const [liveConfig, setLiveConfig] = useState<LiveConfig | null>(null)
  const [snackbarMessage, setSnackbarMessage] = useState('Workout deleted')
  const [purchasing, setPurchasing] = useState(false)
  const [openDeleteDialog, setOpenDeleteDialog] = useState<number | false>(
    false,
  )
  const [openEditDialog, setOpenEditDialog] = useState<ILiveWorkout | false>(
    false,
  )

  const isTrainer = session.isTrainer()
  const userId = getId()

  if (isTrainer && !GATSBY_LIVE_FEATURE_TOGGLE) {
    navigate(TRAINER_RECORDINGS)
  }

  const getWorkouts = async () => {
    const response = await fetchLiveWorkouts()
    setWorkouts(response)
    setLoading(false)
  }

  const getLiveConfig = async () => {
    const response = await fetchLiveConfig()
    setLiveConfig(response)
  }

  useEffect(() => {
    getWorkouts()
    if (GATSBY_API_BASE_CLIENT) {
      getLiveConfig()
    }
  }, [])

  const handlePurchase = (workout: ILiveWorkout) => {
    setPurchasing(true)
    signupLiveWorkout(workout.id as number).then(async () => {
      setSnackbarMessage('You signed up for a workout')
      setOpenSnackbar(true)
      await getWorkouts()
      setPurchasing(false)
    })
  }

  const getWorkoutsFiltered = () => {
    if (workouts === undefined) {
      return []
    }
    const workoutsFiltered =
      props.trainerId !== undefined
        ? workouts.filter(item => item.trainer.id === props.trainerId)
        : workouts

    if (props.userProfileId !== undefined) {
      return workouts.filter(item => {
        const count = item.usersAttending.filter(
          user => user.id === props.userProfileId,
        )
        return count.length === 1
      })
    }
    return workoutsFiltered
  }

  const filterWorkoutsByDate = (daysToAdd: number) => {
    if (selectedDate === null) {
      return workouts
    }
    const workoutsFiltered = getWorkoutsFiltered()

    return workoutsFiltered.filter(workout => {
      const startDate = DateTime.fromISO(workout.startDate)

      const diff = startDate
        .startOf('day')
        .diff(selectedDate.startOf('day'), 'days')
        .toObject()

      if (
        diff.days !== undefined &&
        diff.days >= daysToAdd &&
        diff.days < daysToAdd + 1
      ) {
        if (isTrainer) {
          if (workout.trainer.id === userId) {
            return workout
          }
        } else {
          return workout
        }
      }
    })
  }

  const goToEdit = (workout: ILiveWorkout, hasPayingCustomers: boolean) => {
    if (!hasPayingCustomers) {
      navigate(TRAINER_EDIT_WORKOUT, {
        state: { workoutData: workout },
      })
    } else {
      setOpenEditDialog(workout)
    }
  }

  const handleDeleteWorkout = (
    workoutId: number | string,
    hasPayingCustomers: boolean,
  ) => {
    setOpenDeleteDialog(workoutId as number)
  }

  const deleteWorkout = () => {
    const id = openDeleteDialog as number
    deleteLiveWorkout(id).then(() => {
      setOpenSnackbar(true)
      setOpenDeleteDialog(false)
      getWorkouts()
    })
  }

  const handleClose = () => {
    setOpenDeleteDialog(false)
  }

  const onSelectedDay = (date: string) => {
    const dateTime = DateTime.fromJSDate(new Date(date)) || null
    setSelectedDate(dateTime)
  }

  const isEmpty = () => {
    let numberOfWorkouts = 0
    daysAddedToSelectedDate.map((day, indexOrg) => {
      numberOfWorkouts += filterWorkoutsByDate(indexOrg).length
    })
    return numberOfWorkouts === 0 ? true : false
  }

  const handleClickActions = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
  }

  const handleGoToDetail = (workout: ILiveWorkout) => {
    navigate(
      isTrainer
        ? LIVE_WORKOUT_DETAIL(workout.id)
        : ATHLETE_LIVE_WORKOUT_DETAIL(workout.id),
      {
        state: {
          workoutDetailData: workout,
        },
      },
    )
  }

  const goToTrainerDetail = (trainer: TrainerResponse) => {
    navigate(TRAINER_DETAIL(trainer.id), {
      state: { trainer },
    })
  }

  const getHasPayingCustomers = (workoutId: number) => {
    const workoutObject = workouts.filter(w => w.id === workoutId)[0]
    return (
      workoutObject &&
      workoutObject.isForSale &&
      workoutObject.usersAttending.length > 0
    )
  }

  const isLocked = !props.userHasAccess && !isTrainer

  return (
    <ThemeProvider theme={theme}>
      <S.Container>
        <S.Wrap fullWidth={props.fullWidth}>
          {props.userProfileId !== undefined && (
            <S.SectionHeader>Scheduled Live Workouts</S.SectionHeader>
          )}
          {props.trainerId === undefined && props.userProfileId === undefined && (
            <S.CalendarSection>
              <ReactHorizontalDatePicker
                // @ts-ignore
                selectedDay={onSelectedDay}
                enableScroll={false}
                enableDays={180}
              />
            </S.CalendarSection>
          )}

          <S.ClassesSection>
            {(loading || purchasing) && (
              <S.LoaderWrap>
                <CircularProgress />
              </S.LoaderWrap>
            )}

            {!loading && isEmpty() && (
              <S.EmptyState>
                {isTrainer
                  ? 'No workouts are scheduled for this time period. Schedule a workout and it will appear here!'
                  : 'No workouts are scheduled for this time period. Check back soon!'}
              </S.EmptyState>
            )}

            {daysAddedToSelectedDate.map((day, indexOrg) => {
              const filteredWorkouts = filterWorkoutsByDate(indexOrg)
              const dayPlus = selectedDate?.plus({ days: indexOrg })

              return (
                <React.Fragment key={`${day}.${indexOrg}`}>
                  {filteredWorkouts.length > 0 && (
                    <S.DateTitle>
                      {dayPlus && dayPlus.toFormat('cccc')}
                      {', '}
                      {dayPlus && dayPlus.toFormat('LLL d')}
                    </S.DateTitle>
                  )}
                  {filteredWorkouts.map((workout: ILiveWorkout, index) => {
                    const startDateFormatted = DateTime.fromISO(
                      workout.startDate,
                    ).toLocaleString(DateTime.TIME_SIMPLE)

                    const userSignedUp =
                      workout.usersAttending.filter(user => userId === user.id)
                        .length > 0

                    const hasPayingCustomers =
                      workout.isForSale && workout.usersAttending.length > 0

                    return (
                      <S.RowWrap key={workout.id}>
                        <S.ClassImage
                          src={workout.trainer.images.small}
                          // @ts-ignore
                          onClick={() => goToTrainerDetail(workout.trainer)}
                        />
                        <S.ClassRow
                          id={`scheduleRow${workout.title}`}
                          key={workout.id}
                          onClick={() => handleGoToDetail(workout)}
                          hasBorder={filteredWorkouts.length - 1 !== index}
                        >
                          <S.ClassDesc>
                            {workout.title}
                            <br />
                            <S.SmallDesc>
                              with {workout.trainer.firstName}{' '}
                              {workout.trainer.lastName}
                            </S.SmallDesc>
                            <S.ClassTime>
                              {`${startDateFormatted} - ${workout.length}m`}
                            </S.ClassTime>
                          </S.ClassDesc>

                          <S.ClassActions onClick={handleClickActions}>
                            {isTrainer && (
                              <S.HoverIcons
                                isTest={workout.title.includes(
                                  'TestCypressTitle',
                                )}
                              >
                                <Tooltip title={'Edit Workout'}>
                                  <IconButton
                                    id={`editLwButton${workout.title}`}
                                    onClick={() =>
                                      goToEdit(workout, hasPayingCustomers)
                                    }
                                  >
                                    <EditIcon
                                      style={{
                                        color: Colors.themeColors.textColor,
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title={'Delete Workout'}>
                                  <IconButton
                                    style={{ marginRight: -5 }}
                                    id={`deleteLwButton${workout.title}`}
                                    onClick={() =>
                                      handleDeleteWorkout(
                                        workout.id,
                                        hasPayingCustomers,
                                      )
                                    }
                                  >
                                    <DeleteIcon
                                      style={{
                                        color: Colors.themeColors.textColor,
                                      }}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </S.HoverIcons>
                            )}

                            <UsersAttending workout={workout} />
                            <JoinButton
                              isLocked={isLocked}
                              workout={workout}
                              userSignedUp={userSignedUp}
                              isTrainer={isTrainer}
                              handlePurchase={handlePurchase}
                              liveConfig={liveConfig}
                              userHasAccess={props.userHasAccess}
                            />
                          </S.ClassActions>
                        </S.ClassRow>
                      </S.RowWrap>
                    )
                  })}
                </React.Fragment>
              )
            })}
          </S.ClassesSection>
        </S.Wrap>

        <Dialog
          open={openDeleteDialog ? true : false}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Delete Live Class</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {getHasPayingCustomers(openDeleteDialog as number)
                ? 'People have already scheduled and/or purchased this class. Would you still like to delete the live class?'
                : 'Are you sure you want to delete this live class ?'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button
              onClick={deleteWorkout}
              color="primary"
              autoFocus
              id="deleteLwConfirmButton"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openEditDialog ? true : false}
          onClose={() => setOpenEditDialog(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Edit Live Class</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              People have already scheduled and/or purchased this class. Would
              you still like to edit the live class?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditDialog(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => goToEdit(openEditDialog as ILiveWorkout, false)}
              color="primary"
              autoFocus
              id="deleteLwConfirmButton"
            >
              Edit
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          open={openSnackbar}
          onClose={() => setOpenSnackbar(false)}
          message={snackbarMessage}
          autoHideDuration={3000}
        />
      </S.Container>
    </ThemeProvider>
  )
}

const mapStateToProps = (state: RootState) => ({
  userHasAccess: state.ui.userAccessReducer.userHasAccess,
})

export default connect(mapStateToProps, null)(ScheduleComponent)
